import angular from 'angular';
import { sprintf } from '@/angular/libs/sprintf';
import _ from "lodash";
import { getValueFormatter, getValueFormatterWithDecimals } from '@/angular/utils/graphUtils';

export const vrmEssGraphModule = angular.module('vrm.ess-graph', []);

angular.module('vrm.ess-graph')
    .service("EssGraphService", EssGraphService);

/**
 * @constructor
 * @ngInject
 */
function EssGraphService(WhitelabelService, moment, vrmConfig, locale, $filter) {

    const colors = {
        BLUE: 0,
        RED: 1,
        GREEN: 2,
        ORANGE: 3,
        INDIGO: 4,
        DARKBLUE: 5,
        BLACK: 6,
        PURPLE: 7,
        TRANSPARENT: 8,
        LIGHT_BLUE: 9,
        LIGHT_RED: 10,
        LIGHT_GREEN: 11,
        LIGHT_ORANGE: 12,
        LIGHT_PINK: 13,
        DARK_BLUE: 14,
        MAGENTA: 15,
        CYAN: 16,
        LEMON_GREEN: 17,
    };

    const keepNegativeValue = [
        'buy_price',
        'sell_price',
    ];

    const CHART_INTERVAL = 3600;

    return {
        addLiveFeedForecast: addLiveFeedForecast,
        addEssData: addEssData,
        addEssPricesData: addEssPricesData,
        getTooltipFormatter: getTooltipFormatter,
        getColumnColor: getColumnColor,
        getDynamicEssEarnings: getDynamicEssEarnings,
        colors: colors
    };

    /**
     * Get the striped pattern fill for forecast columns
     *
     * @param {string} color
     */
    function getGraphFill(color) {

        // Assign unique ID to trick Highcharts into rendering the graph fill correctly
        return {
            pattern: {
                id: Math.floor(Math.random() * 1000) + '-fill-' + color,
                path: {
                    d: "M 5 0 L 0 5 M -1 1 L 1 -1 M 6 4 L 4 6",
                    strokeWidth: 2,
                },
                width: 5,
                height: 5,
                color: color,
                borderColor: color
            }
        }
    }

    function preprocessDataRecords(data, currentHour) {
        const records = JSON.parse(JSON.stringify(data.records));

        Object.keys(records).forEach(key => {
            // Convert all records that start with `vrm_` from Wh to kWh
            if ((key.startsWith('vrm_') || key.endsWith('_forecast')) && Array.isArray(records[key])) {
                records[key] = records[key].map(([timestamp, value]) => [timestamp, value / 1000]);
            }

            if ((key.endsWith('_fc') || key.endsWith('_forecast')) && Array.isArray(records[key])) {
                records[key] = records[key].filter(([timestamp]) => timestamp >= currentHour);
            }

            if (!(key.endsWith('_fc') || key.endsWith('_forecast') || key.startsWith('de')) && Array.isArray(records[key])) {
                records[key] = records[key].filter(([timestamp]) => timestamp < currentHour);
            }
        });

        return records;
    }

    function fillCostsSavingsGraph(chart, records, withForecast, currency)
    {
        let gridSavings = multiplyDatapointArrays(records.deGs, records.grid_history_to)
        let gridCosts = multiplyDatapointArrays(records.deGb, records.grid_history_from)
        let batteryCosts = multiplyDatapointArrays(records.deBc, records.total_battery_flow)
        
        // Split grid costs into positive and negative values
        const positiveGridCosts = gridCosts.filter(el => el[1] >= 0);
        const negativeGridCosts = gridCosts.filter(el => el[1] < 0);
        
        // Positive grid costs are shown as grid costs, negative grid costs are added to grid savings
        gridCosts = positiveGridCosts;
        const gridSavingsToBeAdded = negativeGridCosts.map(el => [el[0], -el[1]]);
        gridSavings = addDataPointArraysTogether(gridSavings, gridSavingsToBeAdded);

        // Force the costs to always be negative numbers
        gridCosts = gridCosts.map(el => [el[0], -Math.abs(el[1])])
        batteryCosts = batteryCosts.map(el => [el[0], -Math.abs(el[1])])

        //To caclulate daily total costs, we sum the second elements of the inner array
        const calculateTotalCosts = (arr) =>
        arr.map(innerArray => innerArray[1]).reduce((acc, curr) => acc + curr, 0);
        const totalGridCosts = calculateTotalCosts(gridCosts);
        const totalBatteryCosts = calculateTotalCosts(batteryCosts);
        const totalGridSavings = calculateTotalCosts(gridSavings);
        const totalResult = totalGridSavings + totalBatteryCosts + totalGridCosts; //Costs are already negative!
        const dailyCostsSavings = [totalResult, totalGridSavings, totalBatteryCosts, totalGridCosts];

        chart.series[0].visible = false;
        chart.series[0].data = gridSavings;
        chart.series[0].visible = gridSavings.length > 0;
        chart.series[1].visible = false;
        chart.series[1].data = gridCosts;
        chart.series[1].visible = gridCosts.length > 0;
        chart.series[2].visible = false;
        chart.series[2].data = batteryCosts;
        chart.series[2].visible = batteryCosts.length > 0;

        if (withForecast) {
            // Split vrm_to_grid_fc into positive and negative entries
            const toGrid = [];
            const fromGrid = [];

            Array.isArray(records.vrm_to_grid_fc) && records.vrm_to_grid_fc.forEach(el => {
                if (el[1] > 0) {
                    toGrid.push(el)
                } else {
                    fromGrid.push(el)
                }
            });

            let forecastGridSavings = multiplyDatapointArrays(toGrid, records.deGs)
            let forecastGridCosts = multiplyDatapointArrays(fromGrid, records.deGb)
            let forecastBatteryCosts = multiplyDatapointArrays(records.vrm_to_battery_fc, records.deBc)

            // Force the costs to always be negative numbers
            forecastGridCosts = forecastGridCosts.map(el => [el[0], -Math.abs(el[1])])
            forecastBatteryCosts = forecastBatteryCosts.map(el => [el[0], -Math.abs(el[1])])

            // add forecast data to series
            chart.series[3].data = forecastGridSavings;
            chart.series[3].visible = forecastGridSavings.length > 0;
            // add forecast data to series
            chart.series[4].data = forecastGridCosts;
            chart.series[4].visible = forecastGridCosts.length > 0;
            // add forecast data to series
            chart.series[5].data = forecastBatteryCosts;
            chart.series[5].visible = forecastBatteryCosts.length > 0;
        }

        chart.yAxis[0].title.text = locale.getString(
            'graph.price_with_currency',
            { sign: currency.sign }
        );
        chart.options.tooltip.valuePrefix = currency.sign;

        chart.loading = false;
        return dailyCostsSavings;
    }

    function fillEnergyPricesGraph(chart, records, withForecast, startDate, endDate, currency) {
        const energyBuyPrices = trailLast(_.toArray(records.deGb), CHART_INTERVAL, false);
        const energySellPrices = trailLast(_.toArray(records.deGs), CHART_INTERVAL, false);

        chart.series[0].data = energyBuyPrices;
        chart.series[0].visible = energyBuyPrices.length > 0;
        chart.series[1].data = energySellPrices;
        chart.series[1].visible = energySellPrices.length > 0;

        fixChartBounds(chart, startDate, endDate, 0);

        chart.yAxis[0].title.text = locale.getString(
          'graph.price_with_currency',
          { sign: currency.sign }
        );
        chart.options.tooltip.valuePrefix = currency.sign;

        chart.loading = false;

        if (withForecast) {
            //TODO: show/hide forecasted zone
        }
    }

    function fillUsageGraph(chart, records, withForecast, startDate, endDate) {
        let gridUsage = _.toArray(records.total_grid_flow);
        let batteryUsage = _.toArray(records.total_battery_flow);

        chart.series[0].visible = false;
        chart.series[0].data = gridUsage;
        chart.series[0].visible = gridUsage.length > 0;
        chart.series[1].visible = false;
        chart.series[1].data = batteryUsage;
        chart.series[1].visible = batteryUsage.length > 0;
        if (withForecast) {
            let gridUsageForecast = _.toArray(records.vrm_to_grid_fc);
            let batteryUsageForecast = _.toArray(records.vrm_to_battery_fc);

            chart.series[2].data = gridUsageForecast;
            chart.series[2].visible = gridUsageForecast.length > 0;
            chart.series[3].data = batteryUsageForecast;
            chart.series[3].visible = batteryUsageForecast.length > 0;
        }

        chart.xAxis.softMin = startDate * 1000;
        chart.xAxis.softMax = endDate * 1000;

        chart.loading = false;
    }

    function fillEssAlgorithmGraph(chart, records, withForecast, startDate, endDate, currentHour) {
        let {
            vrm_to_grid_fc,
            vrm_to_battery_fc,
            Pg,
            Pb,
            Gc,
            Bc,
            Gb,
            Bg,
            total_consumption,
            total_solar_yield,
            solar_yield_forecast,
            vrm_consumption_fc,
            vrm_to_grid_def,
        } = records;

        let battery_to_consumption = _.toArray(Bc);
        let grid_to_consumption = _.toArray(Gc);
        let production_to_battery = _.toArray(Pb);
        let production_to_grid = _.toArray(Pg);

        let total_production = _.toArray(total_solar_yield);
        let battery_to_grid = _.toArray(Bg);
        let grid_to_battery = _.toArray(Gb);
        let to_grid_fc = _.toArray(vrm_to_grid_fc);
        let to_battery_fc = _.toArray(vrm_to_battery_fc);
        let consumption_fc = _.toArray(vrm_consumption_fc);
        let production_fc = _.toArray(solar_yield_forecast);

        const lastRecordedTimestamp = Math.min(endDate * 1000, currentHour);

        // Iterate between start and end date with increments of one hour
        // fill missing values with 0 for the variable above
        for (let i = startDate * 1000; i < lastRecordedTimestamp; i += CHART_INTERVAL * 1000) {
            if (Array.isArray(battery_to_consumption) && !battery_to_consumption.find(([timestamp]) => timestamp === i)) {
                battery_to_consumption.push([i, 0]);
            }
            if (Array.isArray(grid_to_consumption) && !grid_to_consumption.find(([timestamp]) => timestamp === i)) {
                grid_to_consumption.push([i, 0]);
            }
            if (Array.isArray(production_to_battery) && !production_to_battery.find(([timestamp]) => timestamp === i)) {
                production_to_battery.push([i, 0]);
            }
            if (Array.isArray(production_to_grid) && !production_to_grid.find(([timestamp]) => timestamp === i)) {
                production_to_grid.push([i, 0]);
            }
            if (Array.isArray(total_consumption) && !total_consumption.find(([timestamp]) => timestamp === i)) {
                total_consumption.push([i, 0]);
            }
            if (Array.isArray(total_production) && !total_production.find(([timestamp]) => timestamp === i)) {
                total_production.push([i, 0]);
            }
            if (Array.isArray(battery_to_grid) && !battery_to_grid.find(([timestamp]) => timestamp === i)) {
                battery_to_grid.push([i, 0]);
            }
            if (Array.isArray(grid_to_battery) && !grid_to_battery.find(([timestamp]) => timestamp === i)) {
                grid_to_battery.push([i, 0]);
            }
            if (Array.isArray(to_grid_fc) && !to_grid_fc.find(([timestamp]) => timestamp === i)) {
                to_grid_fc.push([i, 0]);
            }
            if (Array.isArray(to_battery_fc) && !to_battery_fc.find(([timestamp]) => timestamp === i)) {
                to_battery_fc.push([i, 0]);
            }
            if (Array.isArray(consumption_fc) && !consumption_fc.find(([timestamp]) => timestamp === i)) {
                consumption_fc.push([i, 0]);
            }
            if (Array.isArray(production_fc) && !production_fc.find(([timestamp]) => timestamp === i)) {
                production_fc.push([i, 0]);
            }
        }

        Array.isArray(battery_to_consumption) && battery_to_consumption.sort((a, b) => a[0] - b[0]);
        Array.isArray(grid_to_consumption) && grid_to_consumption.sort((a, b) => a[0] - b[0]);
        Array.isArray(production_to_battery) && production_to_battery.sort((a, b) => a[0] - b[0]);
        Array.isArray(production_to_grid) && production_to_grid.sort((a, b) => a[0] - b[0]);
        Array.isArray(total_consumption) && total_consumption.sort((a, b) => a[0] - b[0]);
        Array.isArray(total_production) && total_production.sort((a, b) => a[0] - b[0]);
        Array.isArray(battery_to_grid) && battery_to_grid.sort((a, b) => a[0] - b[0]);
        Array.isArray(grid_to_battery) && grid_to_battery.sort((a, b) => a[0] - b[0]);
        Array.isArray(to_grid_fc) && to_grid_fc.sort((a, b) => a[0] - b[0]);
        Array.isArray(to_battery_fc) && to_battery_fc.sort((a, b) => a[0] - b[0]);
        Array.isArray(consumption_fc) && consumption_fc.sort((a, b) => a[0] - b[0]);
        Array.isArray(production_fc) && production_fc.sort((a, b) => a[0] - b[0]);

        // Pre-process the future values
        let b_fc = to_battery_fc.map(([timestamp, value]) => [timestamp, -value]);
        let g_fc = to_grid_fc.map(([timestamp, value]) => [timestamp, -value]);
        let C = consumption_fc;
        let PV = production_fc;

        // If no solar panels exist, default to 0 values instead of trying to access PV.
        let n_fc = C.map(([timestamp, c], i) => {
            const pv = PV && PV[i] && PV[i].length > 0 ? PV[i][1] : 0;

            return [timestamp, pv - c];
        });


        let to_b_fc = b_fc.map(([timestamp, value]) => { if (value < 0) { return [timestamp, -value] } else { return [timestamp, 0] } });
        let from_b_fc = b_fc.map(([timestamp, value]) => { if (value > 0) { return [timestamp, value] } else { return [timestamp, 0] } });
        let to_g_fc = g_fc.map(([timestamp, value]) => { if (value < 0) { return [timestamp, -value] } else { return [timestamp, 0] } });
        let from_g_fc = g_fc.map(([timestamp, value]) => { if (value > 0) { return [timestamp, value] } else { return [timestamp, 0] } });

        // If no solar panels exist, default to 0 values instead of trying to access PV.
        let production_to_consumption_fc = C.map(([timestamp, value], i) => {
            if (PV && PV[i] && PV[i].length > 0) {
                return [timestamp, Math.min(value, PV[i][1])];
            }

            return [timestamp, 0];
        });

        let grid_to_battery_fc = []
        let battery_to_grid_fc = []

        for (let i = 0; i < b_fc.length; i++) {
            if (Math.sign(b_fc[i][1]) * Math.sign(g_fc[i][1]) >= 0) {
                battery_to_grid_fc.push([b_fc[i][0], 0])
                grid_to_battery_fc.push([b_fc[i][0], 0])
            } else if (Math.sign(b_fc[i][1]) == 1 && Math.sign(n_fc[i][1]) >= 0) {
                battery_to_grid_fc.push([b_fc[i][0], b_fc[i][1]])
                grid_to_battery_fc.push([b_fc[i][0], 0])
            } else if (Math.sign(b_fc[i][1]) == 1 && Math.sign(n_fc[i][1]) == -1) {
                battery_to_grid_fc.push([b_fc[i][0], -g_fc[i][1]])
                grid_to_battery_fc.push([b_fc[i][0], 0])
            } else if (Math.sign(g_fc[i][1]) == 1 && Math.sign(n_fc[i][1]) >= 0) {
                battery_to_grid_fc.push([b_fc[i][0], 0])
                grid_to_battery_fc.push([b_fc[i][0], g_fc[i][1]])
            } else if (Math.sign(g_fc[i][1]) == 1 && Math.sign(n_fc[i][1]) == -1) {
                battery_to_grid_fc.push([b_fc[i][0], 0])
                grid_to_battery_fc.push([b_fc[i][0], -b_fc[i][1]])
            }
        }


        let grid_to_consumption_fc = from_g_fc.map(([timestamp, value], i) => ([timestamp, value - grid_to_battery_fc[i][1]]))
        let battery_to_consumption_fc = from_b_fc.map(([timestamp, value], i) => ([timestamp, value - battery_to_grid_fc[i][1]]))
        let production_to_battery_fc = to_b_fc.map(([timestamp, value], i) => ([timestamp, value - grid_to_battery_fc[i][1]]))
        let production_to_grid_fc = to_g_fc.map(([timestamp, value], i) => ([timestamp, value - battery_to_grid_fc[i][1]]))

        // Minimum between consumption and solar yield
        let production_to_consumption = Array.isArray(total_consumption) ?
            total_consumption.map(([timestamp, value], i) => ([timestamp, value - battery_to_consumption[i][1] - grid_to_consumption[i][1]])) : [];

        // Reverse the values to show financially costly values in opposite direction
        battery_to_consumption = reverseValues(battery_to_consumption);
        grid_to_consumption = reverseValues(grid_to_consumption);
        grid_to_battery = reverseValues(grid_to_battery);
        battery_to_consumption_fc = reverseValues(battery_to_consumption_fc);
        grid_to_consumption_fc = reverseValues(grid_to_consumption_fc);
        grid_to_battery_fc = reverseValues(grid_to_battery_fc);

        const getSeriesItem = (id) => chart.series.find(series => series.id === id) || {};

        getSeriesItem('solar_to_consumption').data = production_to_consumption;
        getSeriesItem('solar_to_consumption').visible = production_to_consumption.length > 0;

        getSeriesItem('battery_to_consumption').data = battery_to_consumption;
        getSeriesItem('battery_to_consumption').visible = battery_to_consumption.length > 0;

        getSeriesItem('grid_to_consumption').data = grid_to_consumption;
        getSeriesItem('grid_to_consumption').visible = grid_to_consumption.length > 0;

        getSeriesItem('solar_to_battery').data = production_to_battery;
        getSeriesItem('solar_to_battery').visible = production_to_battery.length > 0;

        getSeriesItem('solar_to_grid').data = production_to_grid;
        getSeriesItem('solar_to_grid').visible = production_to_grid.length > 0;

        getSeriesItem('battery_to_grid').data = battery_to_grid;
        getSeriesItem('battery_to_grid').visible = battery_to_grid.length > 0;

        getSeriesItem('grid_to_battery').data = grid_to_battery;
        getSeriesItem('grid_to_battery').visible = grid_to_battery.length > 0;

        // Handle zero values and extend line across the last hour
        getSeriesItem('total_solar').data = trailLast(removeMiddleZeros(total_production), CHART_INTERVAL, true);
        getSeriesItem('total_solar').visible = total_production.length > 0;

        getSeriesItem('total_consumption').data = Array.isArray(total_consumption) ?
            trailLast(removeMiddleZeros(total_consumption), CHART_INTERVAL, true) : [];
        getSeriesItem('total_consumption').visible = total_consumption.length > 0;

        if (withForecast) {
            getSeriesItem('solar_to_consumption_fc').data = production_to_consumption_fc;
            getSeriesItem('solar_to_consumption_fc').visible = production_to_consumption_fc.length > 0;

            getSeriesItem('battery_to_consumption_fc').data = battery_to_consumption_fc;
            getSeriesItem('battery_to_consumption_fc').visible = battery_to_consumption_fc.length > 0;

            getSeriesItem('grid_to_consumption_fc').data = grid_to_consumption_fc;
            getSeriesItem('grid_to_consumption_fc').visible = grid_to_consumption_fc.length > 0;

            getSeriesItem('solar_to_battery_fc').data = production_to_battery_fc;
            getSeriesItem('solar_to_battery_fc').visible = production_to_battery_fc.length > 0;

            getSeriesItem('solar_to_grid_fc').data = production_to_grid_fc;
            getSeriesItem('solar_to_grid_fc').visible = production_to_grid_fc.length > 0;

            getSeriesItem('battery_to_grid_fc').data = battery_to_grid_fc;
            getSeriesItem('battery_to_grid_fc').visible = battery_to_grid_fc.length > 0;

            getSeriesItem('grid_to_battery_fc').data = grid_to_battery_fc;
            getSeriesItem('grid_to_battery_fc').visible = grid_to_battery_fc.length > 0;

            // Handle zero values, trail across last hour and connect to historical data
            production_fc = production_fc.filter(([t]) => t >= lastRecordedTimestamp);
            consumption_fc = consumption_fc.filter(([t]) => t >= lastRecordedTimestamp);
            production_fc = trailLast(removeMiddleZeros(production_fc), CHART_INTERVAL, true);
            consumption_fc = trailLast(removeMiddleZeros(consumption_fc), CHART_INTERVAL, true);

            getSeriesItem('total_solar_fc').data = production_fc;
            getSeriesItem('total_solar_fc').visible = production_fc.length > 0;

            getSeriesItem('total_consumption_fc').data = consumption_fc;
            getSeriesItem('total_consumption_fc').visible = consumption_fc.length > 0;

            chart.options.subtitle.text = chart.options.subtitle.textEnabled;
        }

        chart.xAxis.endOnTick = true;
        fixChartBounds(chart, startDate, endDate, CHART_INTERVAL);

        chart.loading = false;
    }

    function getDynamicEssEarnings(data, currentHour) {
        const { recorded, forecasted, disabled } = getDynamicEssEarningsAggregations(data, currentHour);

        return {
            total: sprintf('%.2f', recorded + forecasted),
            recorded: sprintf('%.2f', recorded),
            forecasted: sprintf('%.2f', forecasted),
            disabled: sprintf('%.2f', disabled),
        };
    }

    function getDynamicEssEarningsAggregations(data, currentHour) {

        const records = preprocessDataRecords(data, currentHour);

        // Prices in mu/kWh
        const energyBuyPrices = _.toArray(records.deGb);
        const energySellPrices = _.toArray(records.deGs);
        const batteryCosts = _.toArray(records.deBc);

        // This recorded data is in kWh
        const recordedToGrid = records.grid_history_to !== false ? records.grid_history_to : [];
        const recordedFromGrid = records.grid_history_from !== false ? records.grid_history_from : [];

        const recordedGrid = recordedToGrid.map(([timestamp, value]) => {
            const from = recordedFromGrid.find(([t]) => t === timestamp);
            return [timestamp, value - (from ? from[1] : 0)];
        });

        recordedFromGrid.forEach(([timestamp, value]) => {
            if (!recordedGrid.find(([t]) => t === timestamp)) {
                recordedGrid.push([timestamp, -value]);
            }
        });

        const recordedBattery = (records.total_battery_flow !== false ? records.total_battery_flow : [])

        // This forecasted / simulated data is in Wh
        const forecastedGrid = (records.vrm_to_grid_fc !== false ? records.vrm_to_grid_fc : [])
        const forecastedBattery = (records.vrm_to_battery_fc !== false ? records.vrm_to_battery_fc : [])
        const simulatedGrid = (records.vrm_to_grid_def !== false ? records.vrm_to_grid_def : [])
        const simulatedBattery = (records.vrm_to_battery_def !== false ? records.vrm_to_battery_def : [])

        const lastRecordedHour = Math.max(
            recordedFromGrid.length > 0 ? recordedFromGrid[recordedFromGrid.length - 1][0] : 0,
            recordedToGrid.length > 0 ? recordedToGrid[recordedToGrid.length - 1][0] : 0,
            recordedBattery.length > 0 ? recordedBattery[recordedBattery.length - 1][0] : 0,
        );

        let recorded = 0;
        let forecasted = 0;
        let disabled = 0;

        energyBuyPrices.forEach(([timestamp, buyPrice], i) => {
            let sellPrice = energySellPrices[i];
            sellPrice = sellPrice ? sellPrice[1] : 0;
            let batteryCost = batteryCosts[i];
            batteryCost = batteryCost ? batteryCost[1] : 0;

            if (timestamp <= lastRecordedHour) {
                recorded += getEarningsForTimestamp(timestamp, recordedGrid, recordedBattery, buyPrice, sellPrice, batteryCost);
            } else {
                forecasted += getEarningsForTimestamp(timestamp, forecastedGrid, forecastedBattery, buyPrice, sellPrice, batteryCost);
            }
            disabled += getEarningsForTimestamp(timestamp, simulatedGrid, simulatedBattery, buyPrice, sellPrice, batteryCost);
        });

        // Correct forecasted and simulated as they were in Wh
        return { recorded, forecasted: forecasted, disabled: disabled };
    }

    function getEarningsForTimestamp(timestamp, toGrid, toBattery, buyPrice, sellPrice, batteryCost) {
        let grid = toGrid.find(([t]) => t === timestamp);
        let battery = toBattery.find(([t]) => t === timestamp);
        grid = grid ? grid[1] : 0;
        battery = battery ? battery[1] : 0;

        let earnings = 0;
        earnings += grid > 0 ? grid * sellPrice : grid * buyPrice;
        earnings -= Math.abs(battery) * batteryCost;
        return earnings;
    }

    function reverseValues(data) {
        return data.map(([timestamp, value]) => [timestamp, -value]);
    }

    // Join two data point arrays and sum values where the timestamps match
    function addDataPointArraysTogether(arrayA, arrayB) {
        if (!Array.isArray(arrayA) || !Array.isArray(arrayB)) {
            return [];
        }

        const resultMap = [...arrayA, ...arrayB]
            .reduce((result, [timestamp, value]) => {
                result.set(timestamp, (result.get(timestamp) || 0) + value);
                return result;
            }, new Map());

        return Array.from(resultMap);
    }

    function multiplyDatapointArrays(arrayA, arrayB) {
        if (!arrayA || !arrayB) {
            return [];
        }

        // Deep clone the arrays to lose the references
        arrayA = JSON.parse(JSON.stringify(arrayA));
        arrayB = JSON.parse(JSON.stringify(arrayB));

        let timestampsB = new Set(arrayB.map(el => el[0]))
        arrayA = arrayA.filter(el => timestampsB.has(el[0]))

        return arrayA.map(el => {
            let timestamp = el[0]
            let valueA = el[1]
            let datapointB = arrayB.find(el => el[0] === timestamp)
            let valueB = datapointB[1]

            return [timestamp, valueA * valueB];
        })
    }

    function findMinInDatapointArrays(arrayA, arrayB) {
        if (!arrayA || !arrayB) {
            return [];
        }

        // Deep clone the arrays to lose the references
        arrayA = JSON.parse(JSON.stringify(arrayA));
        arrayB = JSON.parse(JSON.stringify(arrayB));

        let timestampsB = new Set(arrayB.map(el => el[0]))
        arrayA = arrayA.filter(el => timestampsB.has(el[0]))

        return arrayA.map(el => {
            let timestamp = el[0]
            let valueA = el[1]
            let datapointB = arrayB.find(el => el[0] === timestamp)
            let valueB = datapointB[1]

            return [timestamp, Math.min(valueA, valueB)];
        })
    }

    function addEssData(
        charts,
        data,
        withForecast = false,
        startDate = null,
        endDate = null,
        currentHour = null,
        nowStart = null,
        nowEnd = null,
        currency,
    ) {
        // A trick to make the height of graphs scale with container
        charts.costsSavings.size.height = null;
        charts.usage.size.height = null;
        charts.essAlgorithm.size.height = null;

        if (!data || !data.records) {
            return;
        }

        const records = preprocessDataRecords(data, currentHour);

        fillCostsSavingsGraph(charts.costsSavings, records, withForecast, currency);
        fillUsageGraph(charts.usage, records, withForecast, startDate, endDate);
        fillEssAlgorithmGraph(charts.essAlgorithm, records, withForecast, startDate, endDate, currentHour);

        // Set lines and zones when the forecast is enabled
        if (withForecast) {
            charts.costsSavings.xAxis.plotBands[0].from = nowStart;
            charts.costsSavings.xAxis.plotBands[0].to = nowEnd;

            charts.usage.xAxis.plotBands[0].from = nowStart;
            charts.usage.xAxis.plotBands[0].to = nowEnd;

            charts.essAlgorithm.xAxis.plotBands[0].from = nowStart;
            charts.essAlgorithm.xAxis.plotBands[0].to = nowEnd;
        }
        const summarizedResults = fillCostsSavingsGraph(charts.costsSavings, records, withForecast, currency);
        return summarizedResults;
    }

    function addEssPricesData(
        charts,
        data,
        withForecast = false,
        startDate = null,
        endDate = null,
        currentHour = null,
        nowStart = null,
        nowEnd = null,
        currency,
    ) {
        // A trick to make the height of graphs scale with container
        charts.energyPricesSchedule.size.height = null;
        charts.energyPricesEnergy.size.height = null;

        if (!data || !data.records) {
            return;
        }

        const records = preprocessDataRecords(data, currentHour);

        fillEnergyPricesGraph(charts.energyPricesSchedule, records, withForecast, startDate, endDate, currency);
        fillEnergyPricesGraph(charts.energyPricesEnergy, records, withForecast, startDate, endDate, currency);

        // Set lines and zones when the forecast is enabled
        if (withForecast) {
            charts.energyPricesSchedule.series[0].zones[0].value = currentHour;
            charts.energyPricesSchedule.series[1].zones[0].value = currentHour;
            charts.energyPricesSchedule.xAxis.plotBands[0].from = nowStart;
            charts.energyPricesSchedule.xAxis.plotBands[0].to = nowEnd;

            charts.energyPricesEnergy.series[0].zones[0].value = currentHour;
            charts.energyPricesEnergy.series[1].zones[0].value = currentHour;
            charts.energyPricesEnergy.xAxis.plotBands[0].from = nowStart;
            charts.energyPricesEnergy.xAxis.plotBands[0].to = nowEnd;
        }
    }

    /**
     * Get Highcharts fill pattern for forecast column
     * @param color
     * @param forecast
     * @returns {{pattern: {path: {strokeWidth: number, d: string}, color, width: number, id: string, opacity: number, height: number}}}
     */
    function getColumnColor(color, forecast = false) {
        const GraphColors = WhitelabelService.getGraphColors();

        const essGraphColors = {
            [colors.BLUE]: GraphColors.blue,
            [colors.DARK_BLUE]: GraphColors.darkBlue,
            [colors.LIGHT_BLUE]: GraphColors.lightBlue,
            [colors.RED]: GraphColors.red,
            [colors.LIGHT_RED]: GraphColors.lightRed,
            [colors.GREEN]: GraphColors.green,
            [colors.LIGHT_GREEN]: GraphColors.lightGreen,
            [colors.ORANGE]: GraphColors.orange,
            [colors.LIGHT_ORANGE]: GraphColors.lightOrange,
            [colors.INDIGO]: GraphColors.indigo,
            [colors.DARKBLUE]: GraphColors.darkBlue,
            [colors.BLACK]: GraphColors.black,
            [colors.PURPLE]: GraphColors.lightPurple,
            [colors.LIGHT_PINK]: GraphColors.lightPink,
            [colors.CYAN]: GraphColors.cyan,
            [colors.MAGENTA]: GraphColors.magenta,
            [colors.TRANSPARENT]: GraphColors.transparent,
            [colors.DARK_GREY]: GraphColors.darkGrey,
            [colors.LEMON_GREEN]: GraphColors.lemonGreen,
        }

        color = essGraphColors[color];

        if (forecast)
            return getGraphFill(color);

        return color;
    }

    /**
     * Map colors to the Energy Prices barchart based on buy/sell values
     * @param pricesData
     * @param forecast
     * @returns {*}
     */
    function assignColumnColors(pricesData, forecast = false) {
        return pricesData.map((el, index) => {
            if (el[0] !== undefined) {
                el = { x: el[0], y: el[1] };
            }
            if (el.y < 0)
                return { x: el.x, y: el.y, color: getColumnColor(colors.RED, forecast), borderColor: getColumnColor(colors.RED, false) };
            else
                return { x: el.x, y: el.y, color: getColumnColor(colors.GREEN, forecast), borderColor: getColumnColor(colors.GREEN, false) };
        })
    }

    function addLiveFeedForecast(chart, forecastData, index, color = null, onlyForecast = false, from = null, to = null) {
        if (chart.series[index].visible)
            chart.series[index].visible = false;

        if (forecastData.length === 0) return;

        chart.series[index].data = forecastData;

        if (color !== null)
            chart.series[index].borderColor = getColumnColor(color, false)

        if (!onlyForecast)
            chart.xAxis.plotBands[0].from = from;
        chart.xAxis.plotBands[0].to = to;

        chart.series[index].visible = true;
    }

    function getTooltipFormatter(GraphService, unit, excludedSeries = [], showAllDecimals = false) {
        const self = this;

        return {
            useHTML: true,
            headerFormat: '<table>',
            footerFormat: '</table>',
            formatter: function (tooltip) {
                const items = this.points || self.highcharts.splat(this);
                const usedItems = [];
                const output = [tooltip.tooltipFooterHeaderFormatter(items[0])]; // Build the header

                let startDate = null;
                let endDate = null;

                const range = this.points[0].series.closestPointRange;

                if (!range || range <= 1) return false;

                const oneDay = 60 * 60 * 24 * 1000;
                const format = range >= oneDay ? 'yyyy-MM-dd HH:mm' : 'HH:mm';
                if (range > 27 * oneDay) {
                    startDate = this.moment(this.points[0].x);
                    endDate = this.moment(this.points[0].x).endOf("month");
                    if (endDate.diff(startDate) < oneDay) {
                        startDate.add(1, "M");
                        endDate.add(1, "M");
                    }
                    startDate = startDate.startOf("month").toDate();
                    endDate = endDate.toDate();
                } else {
                    startDate = this.points[0].x;
                    endDate = startDate + range - 1000; // Remove one second to make clear it's not inclusive

                    startDate = GraphService.getTimeInTimezone(startDate);
                    endDate = GraphService.getTimeInTimezone(endDate);
                }

                startDate = $filter("date")(startDate, format);
                endDate = $filter("date")(endDate, format);

                if (startDate && endDate) {
                    output.push('<tr><td class="highcharts-tooltip__title" colspan="2">' + startDate + ' - ' + endDate + '</td></tr>');
                }

                const prefix = tooltip.options.valuePrefix || '';
                const suffix = tooltip.options.valueSuffix || unit || '';
                const valueFormatter = showAllDecimals ? getValueFormatterWithDecimals(prefix, suffix) : getValueFormatter(prefix, suffix, 2);

                // order items by item.series.options.index if it exists
                items.sort((a, b) => {
                    if (a.series.options.index < b.series.options.index) return 1;
                    if (a.series.options.index > b.series.options.index) return -1;
                    return 0;
                });

                let total = 0;
                items.forEach(item => {
                    const seriesId = item.series.userOptions.id;

                    if (
                        typeof seriesId === 'undefined' ||
                        excludedSeries.includes(seriesId) || // Skip excluded series
                        item.point.y === 0 || // Skip zero values
                        usedItems.includes(seriesId) // Skip duplicate series
                    ) return;

                    usedItems.push(seriesId);
                    usedItems.push(seriesId + '_fc');

                    const isHovered = item.series.chart.hoverPoint.series.userOptions.id === seriesId
                    const isForecast = seriesId.includes('_fc');
                    // Show negative value in label only for certain series
                    const onlyPositive = !keepNegativeValue.includes(seriesId);
                    const valueWithUnit = valueFormatter(item.point.y, onlyPositive);


                    let color = item.point.color;
                    if (typeof color === 'object' && typeof color.pattern !== 'undefined') {
                        color = color.pattern.color;
                    }

                    // Conditionally format grid costs/earnings
                    if (seriesId === 'grid_costs' || seriesId === 'grid_costs_fc') {
                        // Custom formatting for grid costs/savings
                        if (item.point.y > 0) {
                            output.push(GraphService.getTablePointFormat(getColumnColor(colors.GREEN, false), locale.getString('graph.grid_savings'), valueWithUnit, isHovered, isForecast));
                        } else {
                            output.push(GraphService.getTablePointFormat(getColumnColor(colors.RED, false), locale.getString('graph.grid_costs'), valueWithUnit, isHovered, isForecast));
                        }
                    } else {
                        output.push(GraphService.getTablePointFormat(color, item.series.name, valueWithUnit, isHovered, isForecast));
                    }

                    // Compute total
                    const costSavingsGraphSeries = [
                        'grid_costs',
                        'grid_costs_fc',
                        'grid_savings',
                        'grid_savings_fc',
                        'battery_costs',
                        'battery_costs_fc'
                    ];
                    if (costSavingsGraphSeries.includes(seriesId)){
                        total += item.point.y;
                    }
                });

                const essAlgorithmGraphTotalSeries = [
                    'total_consumption',
                    'total_solar',
                    'total_consumption_fc',
                    'total_solar_fc',
                ];
                const essAlgorithmGraphTotals = items.filter(item => essAlgorithmGraphTotalSeries.includes(item.series.userOptions.id));
                if (essAlgorithmGraphTotals.length > 0) {
                    output.push('<tr><td><br></td></tr>');
                    essAlgorithmGraphTotals.forEach(item => {
                        if (item.point.y === 0) return;
                        const totalWithUnit = valueFormatter(item.point.y, false);
                        output.push(
                            `<tr>
                                <td><b>${item.series.name}</b></td>
                                <td><b>${totalWithUnit}</b></td>
                            </tr>`
                        );
                    });
                }

                // totals
                if (total !== 0){
                    const totalWithUnit = valueFormatter(total, false)
                    output.push(GraphService.getTableTotalFormat(totalWithUnit));
                }

                // footer
                output.push(tooltip.tooltipFooterHeaderFormatter(items[0], true));
                return output;
            },
        }
    }

    function removeMiddleZeros(data) {
        if (!Array.isArray(data) || data.length === 0) return data;

        const result = [];
        data.forEach(([t, value], i) => {
            if (value !== 0) return result.push([t, value]);

            const previousPoint = data[i - 1];
            const previousValue = previousPoint ? previousPoint[1] : 0;

            const nextPoint = data[i + 1];
            const nextValue = nextPoint ? nextPoint[1] : 0;

            if (previousValue !== 0) {
                result.push([t, 0]);
                result.push([t + 1, null]);
            }

            if (nextValue !== 0) {
                result.push([nextPoint[0] - 1, 0]);
            }
        });

        return result;
    }

    function trailLast(data, interval, trailNull) {
        if (!Array.isArray(data) || data.length === 0) return data;

        const lastPoint = data[data.length - 1];
        const nextTimestamp = lastPoint[0] + (interval - (trailNull ? 1 : 0)) * 1000;
        data.push([nextTimestamp, lastPoint[1]]);
        if (trailNull) data.push([nextTimestamp + 1, null]);

        return data;
    }

    function fixChartBounds(chart, start, end, adjustment) {
        chart.xAxis.min = start * 1000;
        chart.xAxis.max = (end - adjustment) * 1000;
    }
}
